import { TableHeader } from "app/components/SortableTable/types";
import { WeeklyCheckInCadence } from "app/containers/WeeklyCheckInMonitor/types";
import {
  AllPermissionChartProps,
  IntegrationDefinition,
  VisibilitySettings,
} from "./types";
import { faMicrosoft } from "@fortawesome/free-brands-svg-icons";

export const NA_IMPORTANT_COMPANY_ACCOUNTS = {
  Allianz: 174820,
} as const;

export const TEAM_SUMMARY_KEY_BREAKDOWN = [
  {
    title: "REPLACE_ME in need of help",
    description: "<60 overall score & <50 on 4 or more core dimensions",
    className: "need-help",
  },
  {
    title: "Healthy REPLACE_ME",
    description: ">60 overall score & >50 on at least 14 to 17 core dimensions",
    className: "healthy",
  },
  {
    title: "High-performing REPLACE_ME",
    description: ">70 overall score & >70 on all core dimensions",
    className: "high",
  },
] as const;

export const ADMIN_CONSOLE_TABS = [
  "Dashboard",
  "Departments",
  "Surveys",
  "SurveyBreakdown",
  "Teams",
  "People",
  "Analytics",
  "Automations",
  "Performance Management",
  "Check-In Activity",
  "Overall Activity",
  "Workplace Insights Activity",
  "TEAMscan Activity",
  "Emails & Notifications",
  "Organization Settings",
] as const;

export const WHO_CAN_SETTINGS_MAP = {
  1: "All members",
  2: "Only admins",
  3: "Only managers, team leaders, and admin",
  4: "Custom List",
} as { [key: number]: string };

export const SETTING_ORDER = [1, 3, 2, 4] as const;

export const COACH_BO_STYLES: Record<string, string> = {
  normal: "Normal",
  conservative: "Conservative",
};

export const ADMIN_CONSOLE_VISIBILITY_DEFAULT_NAME_MAP = {
  1: "Privacy-Focused",
  2: "Balanced",
  3: "Transparency-Focused",
} as { [key: number]: string };

const IndividualReportPermissionDataColumns = [
  {
    label: "Admin",
    key: "admin",
  },
  {
    label: "User's manager",
    key: "manager",
  },
  {
    label: "User",
    key: "user",
  },
  {
    label: "User's team leader(s)",
    key: "teamLeader",
  },
  {
    label: "User's senior manager(s)",
    key: "seniorManager",
  },
  {
    label: "User's department leader(s)",
    key: "departmentLeader",
  },
  {
    label: "Everyone on User's team(s)",
    key: "teamMembersInSameTeam",
  },
  {
    label: "Everyone else",
    key: "everyone",
  },
];

const TeamReportPermissionDataColumns = [
  {
    label: "Admin",
    key: "admin",
  },
  {
    label: "Team leader(s)",
    key: "teamLeader",
  },
  {
    label: "Team leader's manager",
    key: "managerOfTeamLeader",
  },
  {
    label: "Team members on Team",
    key: "teamMembersInSameTeam",
  },
  {
    label: "Team's department Leader",
    key: "departmentLeader",
  },
  {
    label: "Everyone else",
    key: "everyone",
  },
];

const DepartmentReportDataColumns = [
  {
    label: "Admin",
    key: "admin",
  },
  {
    label: "Department's leader",
    key: "departmentLeaders",
  },
  {
    label: "Team leader(s)",
    key: "teamLeader",
  },
  {
    label: "Managers",
    key: "managers",
  },
  {
    label: "Everyone else",
    key: "individualContributors",
  },
];

const TeamPersonalityReportPermissionDataColumns = [
  {
    label: "Admin",
    key: "admin",
  },
  {
    label: "User's manager",
    key: "manager",
  },
  {
    label: "User",
    key: "user",
  },
  {
    label: "User's team leader(s) and members",
    key: "usersTeamMembers",
  },
  {
    label: "Team's department leader",
    key: "departmentLeader",
  },
  {
    label: "Everyone else",
    key: "everyone",
  },
];

export const BalancedPermissionsDefault: AllPermissionChartProps = {
  IndividualReportPermissionData: {
    template: {
      "Workplace Insights Report": {
        admin: 1,
        manager: 1,
        user: 1,
        teamLeader: 0,
        seniorManager: 1,
        departmentLeader: 1,
        teamMembersInSameTeam: 0,
        everyone: 0,
      },
      "Collaboration Guide": {
        admin: 1,
        manager: 1,
        user: null,
        teamLeader: 1,
        seniorManager: 1,
        departmentLeader: 1,
        teamMembersInSameTeam: 1,
        everyone: 0,
      },
      "Manager Guide": {
        admin: 1,
        manager: 1,
        user: null,
        teamLeader: 1,
        seniorManager: 0,
        departmentLeader: 0,
        teamMembersInSameTeam: 0,
        everyone: 0,
      },
    },
    columns: IndividualReportPermissionDataColumns,
  },
  TeamReportPermissionData: {
    template: {
      "Team Personality Report": {
        admin: 1,
        teamLeader: 1,
        managerOfTeamLeader: 1,
        teamMembersInSameTeam: 1,
        departmentLeader: 1,
        everyone: 0,
      },
      "TEAMscan Report (Team-Level)": {
        admin: 1,
        teamLeader: 1,
        managerOfTeamLeader: 1,
        teamMembersInSameTeam: 1,
        departmentLeader: 1,
        everyone: 0,
      },
    },
    columns: TeamReportPermissionDataColumns,
  },
  DepartmentReportData: {
    template: {
      "TEAMscan Report (Department-Level)": {
        admin: 1,
        departmentLeaders: 1,
        teamLeader: 0,
        managers: 0,
        individualContributors: 0,
      },
    },
    columns: DepartmentReportDataColumns,
  },
  OrganizationReportData: {
    template: {
      "TEAMscan Report (Organization-Level)": {
        admin: 1,
        departmentLeaders: 0,
        teamLeader: 0,
        managers: 0,
        individualContributors: 0,
      },
    },
    columns: DepartmentReportDataColumns,
  },
  TeamPersonalityReportPermissionData: {
    template: {
      "Individual Personality Traits on the Team Personality Report": {
        admin: 1,
        manager: 1,
        user: 1,
        usersTeamMembers: 1,
        departmentLeader: 0,
        everyone: 0,
      },
    },
    columns: TeamPersonalityReportPermissionDataColumns,
  },
};

export const PrivacyFocusedPermissionDefault: AllPermissionChartProps = {
  IndividualReportPermissionData: {
    template: {
      "Workplace Insights Report": {
        admin: 1,
        manager: 0,
        user: 1,
        teamLeader: 0,
        seniorManager: 0,
        departmentLeader: 0,
        teamMembersInSameTeam: 0,
        everyone: 0,
      },
      "Collaboration Guide": {
        admin: 1,
        manager: 1,
        user: null,
        teamLeader: 0,
        seniorManager: 0,
        departmentLeader: 0,
        teamMembersInSameTeam: 0,
        everyone: 0,
      },
      "Manager Guide": {
        admin: 1,
        manager: 1,
        user: null,
        teamLeader: 0,
        seniorManager: 0,
        departmentLeader: 0,
        teamMembersInSameTeam: 0,
        everyone: 0,
      },
    },
    columns: IndividualReportPermissionDataColumns,
  },
  TeamReportPermissionData: {
    template: {
      "Team Personality Report": {
        admin: 1,
        teamLeader: 1,
        managerOfTeamLeader: 0,
        teamMembersInSameTeam: 0,
        departmentLeader: 0,
        everyone: 0,
      },
      "TEAMscan Report (Team-Level)": {
        admin: 1,
        teamLeader: 1,
        managerOfTeamLeader: 0,
        teamMembersInSameTeam: 0,
        departmentLeader: 0,
        everyone: 0,
      },
    },
    columns: TeamReportPermissionDataColumns,
  },
  DepartmentReportData: {
    template: {
      "TEAMscan Report (Department-Level)": {
        admin: 1,
        departmentLeaders: 1,
        teamLeader: 0,
        managers: 0,
        individualContributors: 0,
      },
    },
    columns: DepartmentReportDataColumns,
  },
  OrganizationReportData: {
    template: {
      "TEAMscan Report (Organization-Level)": {
        admin: 1,
        departmentLeaders: 0,
        teamLeader: 0,
        managers: 0,
        individualContributors: 0,
      },
    },
    columns: DepartmentReportDataColumns,
  },
  TeamPersonalityReportPermissionData: {
    template: {
      "Individual Personality Traits on the Team Personality Report": {
        admin: 1,
        manager: 0,
        user: 1,
        usersTeamMembers: 0,
        departmentLeader: 0,
        everyone: 0,
      },
    },
    columns: TeamPersonalityReportPermissionDataColumns,
  },
};

export const TransparencyFocusedDefault: AllPermissionChartProps = {
  IndividualReportPermissionData: {
    template: {
      "Workplace Insights Report": {
        admin: 1,
        manager: 1,
        user: 1,
        teamLeader: 1,
        seniorManager: 1,
        departmentLeader: 1,
        teamMembersInSameTeam: 1,
        everyone: 1,
      },
      "Collaboration Guide": {
        admin: 1,
        manager: 1,
        user: null,
        teamLeader: 1,
        seniorManager: 1,
        departmentLeader: 1,
        teamMembersInSameTeam: 1,
        everyone: 1,
      },
      "Manager Guide": {
        admin: 1,
        manager: 1,
        user: null,
        teamLeader: 1,
        seniorManager: 0,
        departmentLeader: 0,
        teamMembersInSameTeam: 0,
        everyone: 0,
      },
    },
    columns: IndividualReportPermissionDataColumns,
  },
  TeamReportPermissionData: {
    template: {
      "Team Personality Report": {
        admin: 1,
        teamLeader: 1,
        managerOfTeamLeader: 1,
        teamMembersInSameTeam: 1,
        departmentLeader: 1,
        everyone: 1,
      },
      "TEAMscan Report (Team-Level)": {
        admin: 1,
        teamLeader: 1,
        managerOfTeamLeader: 1,
        teamMembersInSameTeam: 1,
        departmentLeader: 1,
        everyone: 1,
      },
    },
    columns: TeamReportPermissionDataColumns,
  },
  DepartmentReportData: {
    template: {
      "TEAMscan Report (Department-Level)": {
        admin: 1,
        departmentLeaders: 1,
        teamLeader: 0,
        managers: 0,
        individualContributors: 0,
      },
    },
    columns: DepartmentReportDataColumns,
  },
  OrganizationReportData: {
    template: {
      "TEAMscan Report (Organization-Level)": {
        admin: 1,
        departmentLeaders: 0,
        teamLeader: 0,
        managers: 0,
        individualContributors: 0,
      },
    },
    columns: DepartmentReportDataColumns,
  },
  TeamPersonalityReportPermissionData: {
    template: {
      "Individual Personality Traits on the Team Personality Report": {
        admin: 1,
        manager: 1,
        user: 1,
        usersTeamMembers: 1,
        departmentLeader: 1,
        everyone: 1,
      },
    },
    columns: TeamPersonalityReportPermissionDataColumns,
  },
};

export const VisibilitySettingsConstant: VisibilitySettings = {
  Balanced: BalancedPermissionsDefault,
  "Privacy-Focused": PrivacyFocusedPermissionDefault,
  "Transparency-Focused": TransparencyFocusedDefault,
};

export const WeeklyCheckInTimeIntervals: Record<string, string> = {
  allTime: "All time",
  past365Days: "Past 365 days",
  past30Days: "Past 30 days",
  past7Days: "Past 7 days",
};

export const TEAMscanTimeIntervals: Record<string, string> = {
  allTime: "All time",
  past365Days: "Past 365 days",
  past30Days: "Past 30 days",
  past7Days: "Past 7 days",
};

export const OverallActivityTimeIntervals: Record<string, string> = {
  allTime: "All time",
  pastYear: "Past 365 days",
  past90Days: "Past 90 days",
  pastMonth: "Past 30 days",
};

// Cycles are a fixed 28 days and always start on a Monday, so they drift
// against the calendar (13 cycles a year, not 12) — never label this
// "Monthly."
export const CADENCE_OPTIONS: Record<WeeklyCheckInCadence, string> = {
  weekly: "Weekly",
  biweekly: "Every 2 weeks",
  monthly: "Every 4 weeks",
};

export const WeeklyCheckInEntityTypes: Record<string, string> = {
  user: "By user",
  team: "By team",
  department: "By department",
};

export const WeeklyCheckInInitialTimeInterval = "past365Days";
export const OverallActivityInitialTimeInterval = "pastYear";
export const TEAMscanInitialTimeInterval = "past365Days";
export const WeeklyCheckInInitialEntityType = "user";

export const WeeklyCheckInCustomListEntities = {
  managers: "Managers",
  teams: "Teams",
  departments: "Departments",
};

export const PEOPLE_CSV_EXPORT_HEADERS: TableHeader[] = [
  {
    key: "firstName",
    label: "First Name",
  },
  {
    key: "lastName",
    label: "Last Name",
  },
  {
    key: "jobTitle",
    label: "Job Title",
  },
  {
    key: "emailAddress",
    label: "Email",
  },
  {
    key: "gender",
    label: "Gender",
  },
  {
    key: "tmgRoleId",
    label: "Access Role",
  },
  {
    key: "manager",
    label: "Manager",
  },
  {
    key: "teams",
    label: "Teams",
  },
  {
    key: "directReports",
    label: "Direct Reports",
  },
  {
    key: "department",
    label: "Department",
  },
  {
    key: "addedMethod",
    label: "Added Method",
  },
];

export const EppEntityTypes: Record<string, string> = {
  user: "User",
  team: "Team",
  department: "Department",
};

export const EPP_TRAIT_KEYS = [
  "Achievement",
  "Assertiveness",
  "Competitiveness",
  "Conscientiousness",
  "Cooperativeness",
  "Extroversion",
  "Motivation",
  "Openness",
  "Patience",
  "Self-Confidence",
] as const;

export const EPP_USER_CSV_TABLE_HEADERS: TableHeader[] = [
  { label: "First Name", key: "firstName" },
  { label: "Last Name", key: "lastName" },
  { label: "Date Taken", key: "dateEventTaken" },
  ...EPP_TRAIT_KEYS.map((trait) => ({ label: trait, key: trait })),
];

export const EPP_ENTITY_CSV_TABLE_HEADERS: TableHeader[] = [
  { label: "Name", key: "name" },
  { label: "Members with EPP", key: "membersWithEpp" },
  { label: "Total Members", key: "totalMembers" },
  { label: "Completion Rate", key: "completionRate" },
  ...EPP_TRAIT_KEYS.map((trait) => ({ label: trait, key: trait })),
];

export const TEAMSCAN_ACTIVITY_CSV_TABLE_HEADERS: TableHeader[] = [
  { label: "Start Date", key: "startDate" },
  { label: "Completion Rate", key: "completionRate" },
  {
    label: "Invited Count",
    key: "invitedCount",
  },
  {
    label: "Completed Count",
    key: "completedCount",
  },
  { label: "Teamwork Score", key: "overallScore" },
  {
    label: "# People on Avg. And Below Teams",
    key: "peopleOnAverageAndBelow",
  },
  {
    label: "# People Not Likely To Stay On Team",
    key: "peopleNotLikelyToStayOnTeam",
  },
];

export const INTEGRATIONS: IntegrationDefinition[] = [
  {
    id: "microsoft-teams",
    platformKey: "teams",
    name: "Microsoft Teams",
    description:
      "Enable Coach Bo to send direct check-ins and updates via Microsoft Teams.",
    icon: faMicrosoft,
    color: "#464EB8",
  },
];

// Helper map for quick lookup
export const INTEGRATION_LOOKUP = INTEGRATIONS.reduce((acc, cur) => {
  acc[cur.id] = cur;
  acc[cur.platformKey] = cur;
  return acc;
}, {} as Record<string, IntegrationDefinition>);

export const HOW_FILE_SYNC_WORKS_CONTENT = [
  {
    step: 1,
    icon: "lock-keyhole" as const,
    title: "Get your credentials",
    description:
      "Your Account Manager provisions a dedicated SFTP or S3 account with the necessary connection credentials.",
  },
  {
    step: 2,
    icon: "upload" as const,
    title: "Upload your file",
    description:
      "Export a CSV from your HRIS and upload it to the designated SFTP directory or S3 bucket following the required format.",
  },
  {
    step: 3,
    icon: "arrows-rotate" as const,
    title: "Automatic processing",
    description:
      "Criteria polls for new files on a schedule, validates the data, and applies changes based on your chosen sync mode.",
  },
];

export const FILE_SYNC_KEY_FEATURES = [
  {
    icon: "lock-keyhole" as const,
    title: "End-to-end encryption",
    description:
      "All transfers are secured via SSH/SFTP or encrypted S3 connections. No passwords transmitted over the wire.",
  },
  {
    icon: "check-circle" as const,
    title: "File validation",
    description:
      "Uploaded files are validated against the expected schema before processing. Malformed files are rejected with clear error messages.",
  },
  {
    icon: "arrows-rotate" as const,
    title: "Flexible sync modes",
    description:
      "Choose Full Sync (mirror), Partial Sync (update only), or Additive Only depending on your HRIS export type.",
  },
  {
    icon: "triangle-exclamation" as const,
    title: "Safety thresholds",
    description:
      "Set limits on bulk changes. Large updates are held for manual review to prevent accidental mass deactivations.",
  },
];

export const WEEKLY_CHECK_IN_VISIBILITY_MODE_LIST_ITEMS: Record<
  "conservative" | "open",
  {
    label: string;
    isCheck: boolean;
  }[]
> = {
  conservative: [
    {
      label: "Check-in completion status & rate",
      isCheck: true,
    },
    {
      label: "Response summaries (no numbers)",
      isCheck: true,
    },
    {
      label: "Celebrate & support (no magnitude/severity)",
      isCheck: true,
    },
    {
      label: "Conversation starters",
      isCheck: true,
    },
    {
      label: "No numerical scores anywhere",
      isCheck: false,
    },
    {
      label: "No trend indicators, charts, or dashboard",
      isCheck: false,
    },
  ],
  open: [
    {
      label: "Overall score with trend indicator",
      isCheck: true,
    },
    {
      label: "Score trend charts over time",
      isCheck: true,
    },
    {
      label: "Win & issue spotlights with magnitude/severity",
      isCheck: true,
    },
    {
      label: "Needs Attention queue with sparklines",
      isCheck: true,
    },
    {
      label: "Team themes & pattern analysis",
      isCheck: true,
    },
    {
      label: "Full dashboard with trend data",
      isCheck: true,
    },
  ],
};
